import { clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { buildGlyphPath, drawableGlyphs, scaleForHeight } from '../../core/text/glyphDraw';
import type { GlyphInstance } from '../../core/text/layout';
import { addConstraint, addPoint, createSystem, integrate, solve, type VerletSystem } from '../../core/verlet';
import type { RenderTarget, TechniqueContext } from '../types';
import type { RopeValues } from './params';

/**
 * Letters hanging from simulated ropes.
 *
 * Each rope is a chain of Verlet points with the top pinned; the glyph rides on the last
 * point and takes its rotation from the final segment. Because the glyph is drawn from
 * its real outline rather than as a text label, it keeps the chosen typeface — the whole
 * point of hanging *these* letters rather than generic ones.
 */
export interface Rope {
  first: number;
  last: number;
  glyph: GlyphInstance;
  tint: number;
  size: number;
}

export interface RopeState {
  system: VerletSystem;
  ropes: Rope[];
  palette: ResolvedPalette;
  stepsDone: number;
  targetSteps: number;
}

function values(ctx: TechniqueContext): RopeValues {
  return ctx.values as unknown as RopeValues;
}

export function init(ctx: TechniqueContext): RopeState {
  const v = values(ctx);
  const glyphs = drawableGlyphs(ctx.field.glyphs);
  const segments = Math.max(2, v.segments);

  const system = createSystem(glyphs.length * (segments + 1) + 8, glyphs.length * (segments + 1) + 8);
  const ropes: Rope[] = [];

  glyphs.forEach((glyph, index) => {
    const ratio = glyphs.length > 1 ? index / (glyphs.length - 1) : 0.5;

    let anchorX: number;
    switch (v.anchor) {
      case 'spread':
        anchorX = (0.1 + ratio * 0.8) * ctx.width;
        break;
      case 'centre':
        anchorX = ctx.width / 2;
        break;
      case 'original':
        anchorX = glyph.bounds.x + glyph.bounds.width / 2;
        break;
      default:
        anchorX = ((index + 0.5) / glyphs.length) * ctx.width;
    }

    const length = v.length * (1 + ctx.rng.signed(v.lengthJitter));
    const segmentLength = length / segments;

    let previous = addPoint(system, anchorX, 0, 0);
    const first = previous;
    for (let s = 1; s <= segments; s++) {
      // Start the chain slightly off vertical so it has a direction to fall in;
      // a perfectly vertical rope with no wind never begins to swing.
      const id = addPoint(system, anchorX + ctx.rng.signed(2), s * segmentLength, 1);
      addConstraint(system, previous, id, v.stiffness, segmentLength);
      previous = id;
    }

    ropes.push({ first, last: previous, glyph, tint: ctx.rng.next(), size: v.letterSize });
  });

  return {
    system,
    ropes,
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    stepsDone: 0,
    targetSteps: v.steps,
  };
}

export function step(state: RopeState, _dt: number, ctx: TechniqueContext): RopeState {
  if (state.stepsDone >= state.targetSteps) return state;
  const v = values(ctx);
  const perFrame = Math.max(1, Math.round(4 * clamp01(ctx.quality)));

  for (let f = 0; f < perFrame && state.stepsDone < state.targetSteps; f++) {
    const t = state.stepsDone * 0.05 * v.windFrequency;
    const wind = (Math.sin(t) * 0.7 + Math.sin(t * 2.7) * 0.3) * v.wind * 0.4;

    integrate(state.system, { gravityX: 0, gravityY: v.gravity, damping: v.damping, windX: wind, windY: 0 });
    solve(state.system, 6);
    state.stepsDone++;
  }

  return state;
}

export function isComplete(state: RopeState): boolean {
  return state.stepsDone >= state.targetSteps;
}

export function draw(state: RopeState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const { system } = state;

  // Ropes first, so the letters sit on top of their own strings.
  c.strokeStyle = v.ropeColor;
  c.lineWidth = v.ropeWidth;
  c.lineCap = 'round';
  c.lineJoin = 'round';
  c.globalAlpha *= v.opacity;

  const path = new Path2D();
  for (const rope of state.ropes) {
    path.moveTo(system.x[rope.first], system.y[rope.first]);
    for (let id = rope.first + 1; id <= rope.last; id++) path.lineTo(system.x[id], system.y[id]);
  }
  c.stroke(path);

  state.ropes.forEach((rope, i) => {
    const x = system.x[rope.last];
    const y = system.y[rope.last];
    const prev = Math.max(rope.first, rope.last - 1);
    const rotation = v.followRope ? Math.atan2(y - system.y[prev], x - system.x[prev]) - Math.PI / 2 : 0;

    const scale = scaleForHeight(rope.glyph, rope.size);
    // Hang the glyph below the knot rather than centred on it.
    const offsetY = (rope.size / 2) * Math.cos(rotation);
    const offsetX = -(rope.size / 2) * Math.sin(rotation);

    c.fillStyle = state.palette.at(state.ropes.length > 1 ? i / (state.ropes.length - 1) : rope.tint);
    c.fill(buildGlyphPath(rope.glyph, x + offsetX, y + offsetY, scale, rotation), 'nonzero');
  });
}

export function elementCount(state: RopeState): number {
  return state.ropes.length;
}
